import * as Sentry from '@sentry/react'
import {
  isHLSProvider,
  MediaPlayer as BaseMediaPlayer,
  type MediaErrorDetail,
  type MediaErrorEvent,
  MediaPlayerInstance,
  type MediaPlayerProps,
  MediaProvider,
  type MediaProviderAdapter,
} from '@vidstack/react'
import {
  DefaultVideoLayout,
  DefaultVideoLayoutProps,
  DefaultLayoutIcons,
  DefaultLayoutIconProps,
  DefaultLayoutTranslations,
} from '@vidstack/react/player/layouts/default'
import '@vidstack/react/player/styles/default/theme.css'
import '@vidstack/react/player/styles/default/layouts/video.css'
import {
  type ReactNode,
  type VideoHTMLAttributes,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Icon, IconProps } from '@masterplandev/ui'

import { SENTRY_SECTIONS } from '@/core/constants/constants'
import useTranslation from '@/core/hooks/useTranslation'

import { AudioTrackLocale } from './AudioTrackLocale'
import styles from './MediaPlayer.module.css'
import { MediaPlayerError } from './MediaPlayerError'
import { redactSrc } from './redactSrc'

type VideoPlayerProps = Omit<
  MediaPlayerProps,
  'children' | 'onProviderChange'
> & {
  children?: ReactNode
  /**
   * Children that must stay mounted while the error overlay is shown.
   *
   * Regular `children` are suppressed on error, which unmounts any plugin that registers player
   * listeners in an effect — including the forward-seek guard, whose cleanup removes its
   * `media-seek-request` listener while the player root's keyboard controller stays live. Pass
   * such listener-only, null-rendering plugins here instead.
   */
  persistentChildren?: ReactNode
  originalAudioLanguage?: string
  disablePictureInPicture?: boolean
}

/**
 * Media player based on Vidstack Player
 *
 * @see https://www.vidstack.io/docs/player
 */
export const MediaPlayer = forwardRef<MediaPlayerInstance, VideoPlayerProps>(
  function MediaPlayer(
    {
      children,
      persistentChildren,
      originalAudioLanguage,
      disablePictureInPicture,
      src,
      onError,
      ...playerProps
    },
    ref,
  ) {
    const [error, setError] = useState<MediaErrorDetail | null>(null)
    const [reloading, setReloading] = useState(false)
    const providerTypeRef = useRef<string | undefined>(undefined)
    // Deliberately a ref, not the `error` state: React batches, so several `error` events firing in
    // one tick would all still see `error === null` and each report separately.
    const reportedRef = useRef(false)

    // Keyed on the `src` *prop*, not on the rendered value, so the retry round trip below does not
    // retrigger it — which is why the retry handler has to clear `error` itself. A
    // lecture-to-lecture navigation, on the other hand, must not keep a stale overlay.
    useEffect(() => {
      setError(null)
      reportedRef.current = false
    }, [src])

    // The retry round trip: rendering `src=''` for a single commit makes Vidstack drop the loader,
    // tear the provider down and clear `$providerSetup`, so restoring `src` builds a fresh provider
    // and re-attempts the library import. A `key` bump would also re-run the loader, but it would
    // replace the `MediaPlayerInstance` and orphan `useMediaRemote(player)` /
    // `useMediaState('duration', player)` at `LectureContentVideoPlayer.tsx:75-77`, which subscribe
    // through a stable ref whose `.current` mutates without re-running their effects.
    useEffect(() => {
      if (reloading) {
        setReloading(false)
      }
    }, [reloading])

    const handleProviderChange = (provider: MediaProviderAdapter | null) => {
      providerTypeRef.current = provider?.type

      if (isHLSProvider(provider)) {
        // Must be a loader function, not `import HLS from 'hls.js'` — that would pull hls.js into
        // the entry chunk. Vidstack brand-checks the value on `.prototype` to tell a constructor
        // from a loader, so this has to stay an arrow, never a `function` declaration.
        provider.library = () => import('hls.js')
      }
    }

    const handleError = (
      detail: MediaErrorDetail,
      nativeEvent: MediaErrorEvent,
    ) => {
      // Report only the first error of an error state. The overlay appearing does not stop hls.js,
      // so further fatal errors would keep firing with no visible change. A retry resets the flag,
      // so a failed retry does report again — that is signal worth keeping.
      if (!reportedRef.current) {
        reportedRef.current = true

        // Codes 1-3 carry `mediaError` rather than `error`, so synthesise one from the message to
        // keep those events grouping sensibly in Sentry.
        Sentry.captureException(detail.error ?? new Error(detail.message), {
          level: 'error',
          tags: {
            section: SENTRY_SECTIONS.videoPlayer,
            media_error_code: detail.code?.toString() ?? 'unknown',
          },
          extra: {
            message: detail.message,
            code: detail.code,
            src: redactSrc(src),
            provider_type: providerTypeRef.current,
          },
        })
      }

      setError(detail)
      onError?.(detail, nativeEvent)
    }

    const handleRetry = () => {
      setError(null)
      setReloading(true)
      reportedRef.current = false
    }

    return (
      <BaseMediaPlayer
        data-testid="media-player-frame"
        ref={ref}
        storage="media-player"
        className={styles.player}
        {...playerProps}
        // Only `src`, `onError` and `onProviderChange` may sit after the spread: they must win
        // over anything a caller passes, so the CDN-closing handler cannot be overwritten.
        // Never move `data-testid`, `storage` or `className` here — `storage` after the spread
        // would override AIFoundationIntro's deliberate `storage={undefined}`.
        src={reloading ? '' : src}
        onError={handleError}
        onProviderChange={handleProviderChange}>
        <MediaProvider
          mediaProps={
            { disablePictureInPicture } as VideoHTMLAttributes<HTMLVideoElement>
          }
        />
        <AudioTrackLocale originalAudioLanguage={originalAudioLanguage} />
        {persistentChildren}
        {error !== null ? (
          // The children are suppressed while errored: an opaque overlay on top of a live layout
          // would leave its buttons focusable behind it and the buffering spinner animating
          // underneath. `MediaProvider`, `AudioTrackLocale` and `persistentChildren` stay mounted —
          // the first two need the player context, and the last holds listener-only plugins whose
          // guarantees must not lapse just because playback failed.
          <MediaPlayerError code={error.code} onRetry={handleRetry} />
        ) : (
          children
        )}
      </BaseMediaPlayer>
    )
  },
)

function createIcon(iconProps: IconProps) {
  function PlayerIcon(props: DefaultLayoutIconProps) {
    return <Icon size="lg" {...iconProps} className={props.className} />
  }

  PlayerIcon.displayName = 'PlayerIcon'
  return PlayerIcon
}

const playerIcons: DefaultLayoutIcons = {
  AirPlayButton: {
    Default: createIcon({ symbol: 'media-airplay' }),
    Connecting: createIcon({ symbol: 'media-airplay' }),
    Connected: createIcon({ symbol: 'media-airplay' }),
  },
  GoogleCastButton: {
    Default: createIcon({ symbol: 'media-chrome-cast' }),
    Connecting: createIcon({ symbol: 'media-chrome-cast' }),
    Connected: createIcon({ symbol: 'media-chrome-cast' }),
  },
  PlayButton: {
    Play: createIcon({ symbol: 'media-play' }),
    Pause: createIcon({ symbol: 'media-pause' }),
    Replay: createIcon({ symbol: 'media-repeat' }),
  },
  MuteButton: {
    Mute: createIcon({ symbol: 'media-volume-x' }),
    VolumeLow: createIcon({ symbol: 'media-volume-min' }),
    VolumeHigh: createIcon({ symbol: 'media-volume-max' }),
  },
  CaptionButton: {
    On: createIcon({ symbol: 'media-captions-on' }),
    Off: createIcon({ symbol: 'media-captions' }),
  },
  PIPButton: {
    Enter: createIcon({ symbol: 'media-picture-in-picture' }),
    Exit: createIcon({ symbol: 'media-picture-in-picture-exit' }),
  },
  FullscreenButton: {
    Enter: createIcon({ symbol: 'layout-maximize' }),
    Exit: createIcon({ symbol: 'layout-minimize' }),
  },
  SeekButton: {
    Backward: createIcon({ symbol: 'media-skip-back-10' }),
    Forward: createIcon({ symbol: 'media-skip-forward-10' }),
  },
  Menu: {
    ArrowLeft: createIcon({
      symbol: 'arrows-chevron-left',
      size: 'md',
      color: 'mp2.neutralDark.500',
    }),
    ArrowRight: createIcon({
      symbol: 'arrows-chevron-right',
      size: 'md',
      color: 'mp2.neutralDark.500',
    }),
    Audio: createIcon({ symbol: 'media-music-note' }),
    Chapters: createIcon({ symbol: 'editor-dotpoints' }),
    Captions: createIcon({ symbol: 'media-captions', size: 'md' }),
    Settings: createIcon({ symbol: 'general-settings' }),
    QualityUp: createIcon({ symbol: 'general-plus', size: 'md' }),
    QualityDown: createIcon({ symbol: 'general-minus', size: 'md' }),
    SpeedUp: createIcon({ symbol: 'general-plus', size: 'md' }),
    SpeedDown: createIcon({ symbol: 'general-minus', size: 'md' }),
    FontSizeUp: createIcon({ symbol: 'general-plus', size: 'md' }),
    FontSizeDown: createIcon({ symbol: 'general-minus', size: 'md' }),
    Accessibility: createIcon({ symbol: 'education-glasses', size: 'md' }),
    AudioBoostUp: createIcon({ symbol: 'media-volume-max', size: 'md' }),
    AudioBoostDown: createIcon({ symbol: 'media-volume-min', size: 'md' }),
    Playback: createIcon({ symbol: 'media-play', size: 'md' }),
    OpacityUp: createIcon({ symbol: 'general-plus', size: 'md' }),
    OpacityDown: createIcon({ symbol: 'general-minus', size: 'md' }),
    RadioCheck: createIcon({ symbol: 'general-check', size: 'md' }),
  },
  KeyboardDisplay: {
    Play: createIcon({ symbol: 'media-play', boxSize: 10 }),
    Pause: createIcon({ symbol: 'media-pause', boxSize: 10 }),
    Mute: createIcon({ symbol: 'media-volume-x', boxSize: 10 }),
    VolumeUp: createIcon({ symbol: 'media-volume-plus', boxSize: 10 }),
    VolumeDown: createIcon({ symbol: 'media-volume-minus', boxSize: 10 }),
    EnterFullscreen: createIcon({ symbol: 'layout-maximize', boxSize: 10 }),
    ExitFullscreen: createIcon({ symbol: 'layout-minimize', boxSize: 10 }),
    EnterPiP: createIcon({ symbol: 'media-picture-in-picture', boxSize: 10 }),
    ExitPiP: createIcon({
      symbol: 'media-picture-in-picture-exit',
      boxSize: 10,
    }),
    CaptionsOn: createIcon({ symbol: 'media-captions-on', boxSize: 10 }),
    CaptionsOff: createIcon({ symbol: 'media-captions', boxSize: 10 }),
    SeekBackward: createIcon({ symbol: 'media-skip-back-10', boxSize: 10 }),
    SeekForward: createIcon({ symbol: 'media-skip-forward-10', boxSize: 10 }),
  },
}

export function VideoLayout(props: Omit<DefaultVideoLayoutProps, 'icons'>) {
  const { translate } = useTranslation('media-player')

  const translations: DefaultLayoutTranslations = useMemo(
    () => ({
      Audio: translate('audio'),
      Auto: translate('auto'),
      Captions: translate('captions'),
      Chapters: translate('chapters'),
      Continue: translate('continue'),
      'Closed-Captions Off': translate('closed-captions-off'),
      'Closed-Captions On': translate('closed-captions-on'),
      Default: translate('default'),
      'Enter Fullscreen': translate('enter-fullscreen'),
      'Enter PiP': translate('enter-pip'),
      'Exit Fullscreen': translate('exit-fullscreen'),
      'Exit PiP': translate('exit-pip'),
      LIVE: translate('live'),
      Mute: translate('mute'),
      Normal: translate('normal'),
      Off: translate('off'),
      Pause: translate('pause'),
      Play: translate('play'),
      Quality: translate('quality'),
      Replay: translate('replay'),
      Reset: translate('reset'),
      Seek: translate('seek'),
      Settings: translate('settings'),
      'Seek Backward': translate('seek-backward'),
      'Seek Forward': translate('seek-forward'),
      'Skip To Live': translate('skip-to-live'),
      Speed: translate('speed'),
      Unmute: translate('unmute'),
      Volume: translate('volume'),
      AirPlay: translate('airplay'),
      'Google Cast': translate('google-cast'),
      Connected: translate('connected'),
      Connecting: translate('connecting'),
      Disconnected: translate('disconnected'),
      'Background Color': translate('background-color'),
      'Background Opacity': translate('background-opacity'),
      'Display Background Color': translate('display-background-color'),
      'Display Background Opacity': translate('display-background-opacity'),
      'Font Family': translate('font-family'),
      'Font Size': translate('font-size'),
      'Font Styles': translate('font-styles'),
      'Text Color': translate('text-color'),
      'Text Opacity': translate('text-opacity'),
      'Text Shadow': translate('text-shadow'),
      Black: translate('black'),
      Blue: translate('blue'),
      Cyan: translate('cyan'),
      Green: translate('green'),
      Magenta: translate('magenta'),
      Red: translate('red'),
      White: translate('white'),
      Yellow: translate('yellow'),
      Fullscreen: translate('fullscreen'),
      PiP: translate('pip'),
      Font: translate('font'),
      Track: translate('track'),
      Loop: translate('loop'),
      Playback: translate('playback'),
      Text: translate('text'),
      Download: translate('download'),
      Announcements: translate('announcements'),
      Accessibility: translate('accessibility'),
      Boost: translate('boost'),
      'Caption Styles': translate('caption-styles'),
      'Captions look like this': translate('captions-look-like-this'),
      Disabled: translate('disabled'),
      'Display Background': translate('display-background'),
      Family: translate('family'),
      'Keyboard Animations': translate('keyboard-animations'),
      Size: translate('size'),
      Color: translate('color'),
      Opacity: translate('opacity'),
      Shadow: translate('shadow'),
      'Text Background': translate('text-background'),
    }),
    [translate],
  )

  return (
    <DefaultVideoLayout
      icons={playerIcons}
      translations={translations}
      {...props}
    />
  )
}
